import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  LayoutDashboard,
  FileSearch,
  Globe,
  Users,
  Tag,
  History,
  Settings,
  Play,
  Menu,
  X,
  LogOut,
  User,
  Mail
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Progress } from './components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Tenders from './pages/Tenders';
import Sites from './pages/Sites';
import Recipients from './pages/Recipients';
import Keywords from './pages/Keywords';
import RunHistory from './pages/RunHistory';
import SettingsPage from './pages/Settings';
import Login from './pages/Login';
import EmailLogs from './pages/EmailLogs';
import TenderDetail from './pages/TenderDetail';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner">
          <FileSearch size={48} className="animate-pulse" />
          <p>Loading TavTenders...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const location = useLocation();
  const { user, logout, isAuthenticated, token } = useAuth();

  // Poll for current run status
  useEffect(() => {
    if (!isAuthenticated) return;

    const pollRunStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/runs/current`);
        const data = await response.json();
        setCurrentRun(data);
        setIsRunning(data.status === 'running');
        
        if (data.status === 'completed' && isRunning) {
          toast.success('Tender scout completed!', {
            description: data.message
          });
        } else if (data.status === 'failed' && isRunning) {
          toast.error('Tender scout failed', {
            description: data.message
          });
        }
      } catch (error) {
        console.error('Error polling run status:', error);
      }
    };

    const interval = setInterval(pollRunStatus, 2000);
    pollRunStatus();
    return () => clearInterval(interval);
  }, [isRunning, isAuthenticated]);

  const handleRunNow = async () => {
    try {
      setIsRunning(true);
      const response = await fetch(`${API_URL}/api/runs/trigger`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        toast.info('Tender scout started', {
          description: 'Scraping tenders from enabled sites...'
        });
      } else {
        const error = await response.json();
        toast.error('Failed to start', { description: error.detail });
        setIsRunning(false);
      }
    } catch (error) {
      toast.error('Error', { description: 'Failed to trigger run' });
      setIsRunning(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.info('Logged out successfully');
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/tenders', icon: FileSearch, label: 'Tenders' },
    { path: '/sites', icon: Globe, label: 'Sites' },
    { path: '/recipients', icon: Users, label: 'Recipients' },
    { path: '/keywords', icon: Tag, label: 'Keywords' },
    { path: '/history', icon: History, label: 'Run History' },
    { path: '/emails', icon: Mail, label: 'Email Logs' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  // Don't render the main layout on login page
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <div className="app-container">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon">
              <FileSearch size={24} />
            </div>
            <div>
              <h1 className="logo-text">TavTenders</h1>
              <p className="logo-subtitle">Tender Intelligence</p>
            </div>
          </div>
          <button 
            className="sidebar-close md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="company-badge">
            <span>Alephtav & Mau Tech</span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-wrapper">
        {/* Top bar */}
        <header className="topbar">
          <div className="topbar-left">
            <button 
              className="menu-button md:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} />
            </button>
            <h2 className="page-title">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h2>
          </div>

          <div className="topbar-right">
            {isRunning && currentRun && (
              <div className="run-progress" data-testid="run-progress-indicator">
                <Progress value={currentRun.progress || 0} className="w-32" />
                <span className="run-message">{currentRun.message}</span>
              </div>
            )}
            <Button
              onClick={handleRunNow}
              disabled={isRunning}
              className="run-now-button"
              data-testid="run-now-button"
            >
              <Play size={16} />
              <span>{isRunning ? 'Running...' : 'Run Now'}</span>
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="user-menu-button" data-testid="user-menu-button">
                  <div className="user-avatar">
                    <User size={18} />
                  </div>
                  <span className="user-name hidden md:inline">{user?.username?.split('@')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="user-menu-content">
                <DropdownMenuLabel>
                  <div className="user-menu-label">
                    <span className="user-menu-email">{user?.username}</span>
                    <span className="user-menu-role">Administrator</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="user-menu-logout"
                  data-testid="logout-button"
                >
                  <LogOut size={16} />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/tenders" element={<ProtectedRoute><Tenders /></ProtectedRoute>} />
            <Route path="/tenders/:id" element={<ProtectedRoute><TenderDetail /></ProtectedRoute>} />
            <Route path="/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
            <Route path="/recipients" element={<ProtectedRoute><Recipients /></ProtectedRoute>} />
            <Route path="/keywords" element={<ProtectedRoute><Keywords /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><RunHistory /></ProtectedRoute>} />
            <Route path="/emails" element={<ProtectedRoute><EmailLogs /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          </Routes>
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginWrapper />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </Router>
    </AuthProvider>
  );
}

// Wrapper to handle login page with auth check
function LoginWrapper() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner">
          <FileSearch size={48} className="animate-pulse" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Login />;
}

export default App;
